import React from "react";
import tokens from "../tokens.json";

/**
 * Mission Dresseur — Canonical Mark (single source of truth).
 *
 * Hardcoded fills read from tokens.json so SVG rendering is deterministic
 * across all browsers (Safari does not resolve `var()` reliably inside SVG
 * presentation attributes). To change a brand colour, edit tokens.json.
 *
 * Concept (designer-senior brief): rounded-square frame, a diamond rotated
 * 45° inside that reads as both a compass rose and a card facing up. The
 * upper-right facet is filled with champagne gold — the "reveal" cue.
 * Optical alignment: centre is lifted +1px to compensate for the gold mass.
 *
 * Props:
 *   - tone : "ink" | "cream" | "mono" | "print"
 *   - bg   : "frame" (default) | "none"
 *   - size : pixel size
 */
const C = tokens.core;
const PALETTES = {
    ink: {
        frame: C.navy.hex,
        glyph: C.cream.hex,
        glyphSoft: C.cream.hex,
        glyphSoftOpacity: 0.7,
        gold: C.champagne.hex,
        midline: C.cream.hex,
        midlineOpacity: 0.22,
        centerPin: C.champagne.hex,
    },
    cream: {
        frame: C.cream.hex,
        glyph: C.navy.hex,
        glyphSoft: C.navy.hex,
        glyphSoftOpacity: 0.65,
        gold: C.champagne.hex,
        midline: C.navy.hex,
        midlineOpacity: 0.18,
        centerPin: C.champagne.hex,
    },
    mono: {
        frame: "currentColor",
        glyph: "var(--md-bg)",
        glyphSoft: "var(--md-bg)",
        glyphSoftOpacity: 0.7,
        gold: "var(--md-bg)",
        midline: "var(--md-bg)",
        midlineOpacity: 0.3,
        centerPin: "var(--md-bg)",
    },
    print: {
        frame: C.navy.hex,
        glyph: "#FFFFFF",
        glyphSoft: "#FFFFFF",
        glyphSoftOpacity: 0.72,
        gold: C.champagne.hex,
        midline: "#FFFFFF",
        midlineOpacity: 0.22,
        centerPin: C.champagne.hex,
    },
};

export function BrandMark({
    tone = "ink",
    bg = "frame",
    size = 48,
    title = "Mission Dresseur",
    className = "",
}) {
    const p = PALETTES[tone] || PALETTES.ink;
    const showFrame = bg === "frame";

    return (
        <svg
            role="img"
            aria-label={title}
            viewBox="0 0 96 96"
            width={size}
            height={size}
            className={className}
            data-testid="brand-mark"
            xmlns="http://www.w3.org/2000/svg"
        >
            <title>{title}</title>
            {showFrame && (
                <rect x="2" y="2" width="92" height="92" rx="26" ry="26" fill={p.frame} />
            )}
            {/* Diamond — 4 facets (centre lifted +1px optically) */}
            <path d="M 48 18 L 70 49 L 48 49 Z" fill={p.gold} />
            <path d="M 70 49 L 48 80 L 48 49 Z" fill={p.glyphSoft} fillOpacity={p.glyphSoftOpacity} />
            <path d="M 48 80 L 26 49 L 48 49 Z" fill={p.glyph} />
            <path d="M 26 49 L 48 18 L 48 49 Z" fill={p.glyph} />
            {/* Midline (subtle fold cue) */}
            <line x1="26" y1="49" x2="70" y2="49" stroke={p.midline} strokeOpacity={p.midlineOpacity} strokeWidth="0.75" strokeLinecap="round" />
            {/* Centre pin */}
            <circle cx="48" cy="49" r="1.4" fill={p.centerPin} />
        </svg>
    );
}

export default BrandMark;
